/* @flow strict */

import * as React from 'react';
import {
  ResponsiveContainer, PieChart, Pie, Sector, Cell, Legend, Tooltip
} from 'recharts';
import Loader from '/client/src/components/Loader';
import _ from 'lodash';
import { mockData } from './mock';
import { currency } from '/client/src/constants/';
import { DefaultContainerContext } from '/client/src/contexts/defaultContainer';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
const renderActiveShape = (props: any): React.Element<'g'> => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle,
    fill, payload } = props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius}
        outerRadius={outerRadius + 10}
        fill={fill}
        style={{ opacity: '.4' }}
      />
    </g>
  );
};

type PropsType = {
  eventId: number | string,
  isNoStatistic: boolean
};

const EventPageDetailStatsTop5 = ({ eventId, isNoStatistic }: PropsType): React.Element<'div'> => {
  const context = React.useContext(DefaultContainerContext);

  const [dataView, setReferralView] = React.useState(null);
  const [dataConversion, setReferralConversion] = React.useState(null);

  const [activeIndex, setActiveIndex] = React.useState(null);
  const onMouseOver = React.useCallback((data, index) => setActiveIndex(index), []);
  const onMouseLeave = React.useCallback((): void => setActiveIndex(null), []);

  const handleLoaded = React.useCallback(async (error: Error, response: any) => {
    if (!error) {
      try {
        const _dataView = [];
        const _dataConversion = [];

        let data = _.get(response, 'data.result.items', []);

        if (isNoStatistic && !data.length) data = mockData.StatsTop5;

        _.map(data, (item: any) => {
          _dataView.push({
            name: item.referral_user_name,
            value: item.view_count
          });
          _dataConversion.push({
            name: item.referral_user_name,
            value: (item.amount - item.amount_refund) / 100,
            currency: currency[item.currency]
          });
        });

        setReferralView(_dataView);
        setReferralConversion(_dataConversion);
      } catch (e) {
        console.log(e);
      }
    }
  }, []);

  const renderCustomToolTip = ({ payload }: any): any => {
    if (!payload.length) return;

    console.log(payload);
    return (
      <div
        style={{
          background: 'rgba(255,255,255, .8)',
          border: `1px solid ${COLORS[activeIndex]}`,
          boxShadow: '0 0 3px rgba(0,0,0,0.5)',
          padding: '5px 10px',
          minWidth: '50px'
        }}
      >
        <div style={{ fontSize: '11px' }}>{payload[0].name}</div>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
          <span
            className='PieLegendItem-dot'
            style={{
              background: COLORS[activeIndex % COLORS.length],
              marginLeft: '0',
              width: '5px',
              height: '5px'
            }}
          />
          {payload[0].payload.currency ? payload[0].payload.currency : '' }{payload[0].value}
        </div>
      </div>
    );
  };

  const renderLegend = (props: any): React.Element<'li'> => {
    const { payload, align } = props;

    return (
      <ul className='PieLegend'>
        {
          _.map(payload, (entry: any, index: number): void => (
            <li
              key={`item-${index}`}
              className={`PieLegendItem ${align === 'right' ? 'is-right' : ''}`}
              onMouseEnter={(): void => setActiveIndex(index)}
              onMouseLeave={(): void => setActiveIndex(null)}
            >
              <span className='PieLegendItem-dot' style={{ background: COLORS[index % COLORS.length] }}/>
              <span className='PieLegendItem-text'>
                  {entry.name} ({entry.currency ? entry.currency : ''}{entry.value})
                </span>
            </li>
          ))
        }
      </ul>
    );
  };

  return (
    <Loader
      url={`/api/v1/1/statistics/events/${eventId}/referral_channel?skip_count=0&max_result_count=5&sorting=view_count+desc`}
      onLoaded={handleLoaded}
    >
      <div className="panel panel-default panel-chart">
      <div className="panel-heading">
        <div className="row">
          <div className="col-xs-6">Top 5 Page Views</div>
          <div className="col-xs-6 text-right">Top 5 Conversions</div>
        </div>
      </div>

      <div className="panel-body">
        <div className="row">
          <div className="col-xs-12">
            <div className="left-legend" style={{ height: '270px', display: 'flex' }}>
              {dataView && dataConversion &&
                <>
                  <ResponsiveContainer width="33.33%">
                    <PieChart margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                      <Legend
                        content={renderLegend}
                        payload={dataView}
                        layout='vertical'
                        align='left'
                        verticalAlign='middle'
                        wrapperStyle={{ width: '100%' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <ResponsiveContainer width="33.33%">
                    <PieChart margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                          <Pie
                            dataKey="value"
                            activeIndex={activeIndex}
                            activeShape={renderActiveShape}
                            data={dataView}
                            startAngle={90}
                            endAngle={270}
                            innerRadius="80%"
                            outerRadius="100%"
                            fill="#8884d8"
                            paddingAngle={2}
                            wrapperStyle={{ right: '0' }}
                            onMouseOver={onMouseOver}
                            onMouseLeave={onMouseLeave}
                          >
                            {
                              _.map(dataView, (entry: {}, index: number): void => (
                                <Cell key={`pie1-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))
                            }
                          </Pie>
                          <Pie
                            dataKey="value"
                            activeIndex={activeIndex}
                            activeShape={renderActiveShape}
                            data={dataConversion}
                            startAngle={-90}
                            endAngle={90}
                            innerRadius="80%"
                            outerRadius="100%"
                            fill="#8884d8"
                            paddingAngle={2}
                            onMouseOver={onMouseOver}
                            onMouseLeave={onMouseLeave}
                          >
                            {
                              _.map(dataConversion, (entry: {}, index: number): void => (
                                <Cell key={`pie2-${index}`} fill={COLORS[index % COLORS.length]}/>
                              ))
                            }
                          </Pie>
                      <Tooltip content={renderCustomToolTip} />
                    </PieChart>
                  </ResponsiveContainer>
                  <ResponsiveContainer width="33.33%">
                    <PieChart>
                      <Legend
                        content={renderLegend}
                        payload={dataConversion}
                        layout='vertical'
                        align='right'
                        verticalAlign='middle'
                        wrapperStyle={{ width: '100%' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </>
              }
            </div>
          </div>

        </div>
      </div>
    </div>
    </Loader>
  );
};

export default EventPageDetailStatsTop5;
